import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  BarChart3,
  Beaker,
  BookOpen,
  Calculator,
  Crosshair,
  Database,
  Layers3,
  Ruler,
} from 'lucide-react'
import { CalibrationTab } from './components/CalibrationTab'
import {
  adoptSimulationEvidence,
  adoptToleranceEvidence,
  candidateSimulationInputFingerprint,
  candidateToleranceInputFingerprint,
  simulationInputFingerprint,
  toleranceInputFingerprint,
  type CandidateRunSubject,
} from './candidateEvidence'
import { CandidatesTab } from './components/CandidatesTab'
import { HorizontalScrollRegion } from './components/HorizontalScrollRegion'
import { ModelTab } from './components/ModelTab'
import { ProjectControls } from './components/ProjectControls'
import {
  QualificationRail,
  type AnalyticalState,
  type EvidenceState,
} from './components/QualificationRail'
import { RaySimulationTab } from './components/RaySimulationTab'
import { RequiredLensTab } from './components/RequiredLensTab'
import { SetupTab } from './components/SetupTab'
import { TolerancesTab } from './components/TolerancesTab'
import { Metric, StatusBadge, formatNumber } from './components/ui'
import { defaultState, emptyState } from './defaults'
import { useSimulationWorker } from './hooks/useSimulationWorker'
import { downloadText, exportProjectJson, parseProjectJson } from './io'
import { calculateAnalyticalDesign, evaluateCandidate } from './physics'
import {
  calibrationFitInputFingerprint,
  createIndexedDbProjectBackend,
  createProjectDocument,
  LatestProjectSaveQueue,
  loadStoredProject,
  projectHasEvidence,
} from './projectStore'
import type { AppState, CalibrationDataset, CalibrationFit, ProjectDocumentV1 } from './types'

type TabId =
  | 'setup'
  | 'required'
  | 'candidates'
  | 'simulation'
  | 'tolerances'
  | 'calibration'
  | 'model'

const tabs: Array<{ id: TabId; label: string; icon: typeof Ruler }> = [
  { id: 'setup', label: 'Setup', icon: Ruler },
  { id: 'required', label: 'Required lens', icon: Calculator },
  { id: 'candidates', label: 'Candidate comparison', icon: Database },
  { id: 'simulation', label: 'Ray simulation', icon: Activity },
  { id: 'tolerances', label: 'Tolerances', icon: BarChart3 },
  { id: 'calibration', label: 'Calibration', icon: Crosshair },
  { id: 'model', label: 'Model and assumptions', icon: BookOpen },
]

function App() {
  const [state, setState] = useState<AppState>(() => structuredClone(defaultState))
  const [activeTab, setActiveTab] = useState<TabId>('setup')
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
    state.candidates[0]?.id ?? null,
  )
  const [calibrationDataset, setCalibrationDatasetState] =
    useState<CalibrationDataset | null>(null)
  const [calibrationFit, setCalibrationFit] = useState<CalibrationFit | null>(null)
  const [calibrationFitFingerprint, setCalibrationFitFingerprint] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [projectError, setProjectError] = useState<string | null>(null)
  const [projectNotice, setProjectNotice] = useState<string | null>(null)
  const [evidenceNotice, setEvidenceNotice] = useState<string | null>(null)
  const [unpersistedEvidence, setUnpersistedEvidence] = useState(false)
  const persistenceBlockedRef = useRef(false)
  const saveGenerationRef = useRef(0)
  const loadedCandidateIdRef = useRef<string | null>(null)
  const simulationSubjectRef = useRef<Extract<CandidateRunSubject, { kind: 'simulation' }> | null>(null)
  const toleranceSubjectRef = useRef<Extract<CandidateRunSubject, { kind: 'tolerance' }> | null>(null)
  const [projectBackend] = useState(createIndexedDbProjectBackend)
  const [saveQueue] = useState(() => new LatestProjectSaveQueue(projectBackend))
  const worker = useSimulationWorker()

  const design = useMemo(() => calculateAnalyticalDesign(state), [state])
  const simulationFingerprint = useMemo(
    () => simulationInputFingerprint(state),
    [state.lcd, state.layers, state.view, state.lens, state.simulationSettings],
  )
  const toleranceFingerprint = useMemo(
    () => toleranceInputFingerprint(state, simulationFingerprint),
    [simulationFingerprint, state.toleranceSettings],
  )
  const calibrationFingerprint = useMemo(
    () => calibrationFitInputFingerprint(state, calibrationDataset),
    [state.lcd, state.layers, state.view, state.lens, calibrationDataset],
  )
  const simulationStale = Boolean(
    worker.simulationResult && worker.simulationFingerprint !== simulationFingerprint,
  )
  const monteCarloStale = Boolean(
    worker.monteCarloResult && worker.monteCarloFingerprint !== toleranceFingerprint,
  )
  const calibrationFitStale = Boolean(
    calibrationFit && calibrationFitFingerprint !== calibrationFingerprint,
  )
  const selectedCandidate = useMemo(
    () => state.candidates.find((candidate) => candidate.id === selectedCandidateId) ?? null,
    [state.candidates, selectedCandidateId],
  )
  const selectedCandidateEvaluation = useMemo(
    () => selectedCandidate ? evaluateCandidate(selectedCandidate, state) : null,
    [selectedCandidate, state],
  )
  const evidenceState = (present: boolean, stale: boolean): EvidenceState =>
    !present ? 'missing' : stale ? 'stale' : 'current'
  const traceRequired =
    state.view.criteria.enabled.crosstalk ||
    state.view.criteria.enabled.zoneCenter ||
    state.view.criteria.enabled.throughput
  const traceEvidence: EvidenceState = selectedCandidateEvaluation?.simulationEvidenceCurrent
    ? 'current'
    : selectedCandidateEvaluation?.simulationEvidenceStale
      ? 'stale'
      : 'missing'
  const toleranceEvidence: EvidenceState = selectedCandidateEvaluation?.monteCarloEvidenceCurrent
    ? 'current'
    : selectedCandidateEvaluation?.monteCarloEvidenceStale
      ? 'stale'
      : 'missing'
  const nominalSource = traceRequired && traceEvidence !== 'current'
    ? selectedCandidateEvaluation?.analyticalStatus
    : selectedCandidateEvaluation?.status
  const candidateNominalState: AnalyticalState =
    nominalSource === 'meets' || nominalSource === 'design-target'
      ? 'pass'
      : nominalSource === 'fails'
        ? 'fail'
        : nominalSource ?? 'insufficient'
  const toleranceRequired = Object.values(state.toleranceSettings.includeCriteria).some(Boolean)
  const qualificationInputs = {
    geometryReady:
      design.source.missing.length === 0 &&
      design.gap.missing.length === 0 &&
      design.gap.invalid.length === 0,
    lensTargetReady: design.requiredPitchMm !== null && design.requiredFocalLengthMm !== null,
    candidateExists: selectedCandidate !== null,
    candidateInputReady: Boolean(selectedCandidateEvaluation && selectedCandidateEvaluation.inputMissing.length === 0),
    candidateNominalState,
    traceRequired,
    traceEvidence,
    toleranceRequired,
    toleranceEvidence,
    toleranceSuccessRate: selectedCandidateEvaluation?.monteCarloEvidenceCurrent
      ? selectedCandidate?.monteCarlo?.successRate ?? null
      : null,
    toleranceSummary: selectedCandidateEvaluation?.monteCarloEvidenceCurrent && selectedCandidate?.monteCarlo
      ? `${formatNumber(selectedCandidate.monteCarlo.successRate * 100, 1)}% met enabled checks across ${selectedCandidate.monteCarlo.trials} trials`
      : null,
    calibrationEvidence: evidenceState(calibrationFit !== null, calibrationFitStale),
  }

  const setCalibrationDataset = (dataset: CalibrationDataset | null) => {
    setCalibrationDatasetState(dataset)
    setCalibrationFit(null)
    setCalibrationFitFingerprint(null)
  }

  const buildCurrentProject = useCallback(() => createProjectDocument({
    state,
    simulation: worker.simulationResult && worker.simulationFingerprint
      ? { result: worker.simulationResult, inputFingerprint: worker.simulationFingerprint }
      : null,
    tolerance: worker.monteCarloResult && worker.monteCarloFingerprint
      ? { result: worker.monteCarloResult, inputFingerprint: worker.monteCarloFingerprint }
      : null,
    calibrationDataset,
    calibrationFit,
    calibrationFitFingerprint,
  }), [
    state,
    worker.simulationResult,
    worker.simulationFingerprint,
    worker.monteCarloResult,
    worker.monteCarloFingerprint,
    calibrationDataset,
    calibrationFit,
    calibrationFitFingerprint,
  ])

  const clearRunSubjects = useCallback(() => {
    loadedCandidateIdRef.current = null
    simulationSubjectRef.current = null
    toleranceSubjectRef.current = null
  }, [])

  const applyProject = useCallback((project: ProjectDocumentV1, enablePersistence = false) => {
    clearRunSubjects()
    saveGenerationRef.current += 1
    if (enablePersistence) persistenceBlockedRef.current = false
    setEvidenceNotice(null)
    setUnpersistedEvidence(projectHasEvidence(project))
    setState(project.state)
    setSelectedCandidateId(project.state.candidates[0]?.id ?? null)
    setCalibrationDatasetState(project.evidence.calibrationDataset?.dataset ?? null)
    setCalibrationFit(project.evidence.calibrationFit?.result ?? null)
    setCalibrationFitFingerprint(project.evidence.calibrationFit?.inputFingerprint ?? null)
    worker.replaceEvidence({
      simulation: project.evidence.simulation,
      tolerance: project.evidence.tolerance,
    })
  }, [clearRunSubjects, worker.replaceEvidence])

  const replaceWorkingState = useCallback((next: AppState) => {
    clearRunSubjects()
    setEvidenceNotice(null)
    setProjectNotice(null)
    setCalibrationDatasetState(null)
    setCalibrationFit(null)
    setCalibrationFitFingerprint(null)
    setUnpersistedEvidence(false)
    worker.replaceEvidence({ simulation: null, tolerance: null })
    setState(next)
    setSelectedCandidateId(next.candidates[0]?.id ?? null)
  }, [clearRunSubjects, worker.replaceEvidence])

  useEffect(() => {
    const subject = simulationSubjectRef.current
    if (
      !subject ||
      !worker.simulationResult ||
      worker.simulationFingerprint !== subject.inputFingerprint
    ) return
    simulationSubjectRef.current = null
    const runAt = new Date().toISOString()
    const preview = adoptSimulationEvidence(
      state,
      subject,
      worker.simulationResult,
      worker.simulationFingerprint,
      runAt,
    )
    if (preview === state) return
    const candidateName = state.candidates.find((candidate) => candidate.id === subject.candidateId)?.name
    setState((current) => adoptSimulationEvidence(
      current,
      subject,
      worker.simulationResult!,
      worker.simulationFingerprint,
      runAt,
    ))
    if (candidateName) setEvidenceNotice(`Trace evidence is ready for ${candidateName}.`)
  }, [worker.simulationResult, worker.simulationFingerprint])

  useEffect(() => {
    const subject = toleranceSubjectRef.current
    if (
      !subject ||
      !worker.monteCarloResult ||
      worker.monteCarloFingerprint !== subject.inputFingerprint
    ) return
    toleranceSubjectRef.current = null
    const runAt = new Date().toISOString()
    const preview = adoptToleranceEvidence(
      state,
      subject,
      worker.monteCarloResult,
      worker.monteCarloFingerprint,
      runAt,
    )
    if (preview === state) return
    const candidateName = state.candidates.find((candidate) => candidate.id === subject.candidateId)?.name
    setState((current) => adoptToleranceEvidence(
      current,
      subject,
      worker.monteCarloResult!,
      worker.monteCarloFingerprint,
      runAt,
    ))
    if (candidateName) setEvidenceNotice(`Robustness evidence is ready for ${candidateName}.`)
  }, [worker.monteCarloResult, worker.monteCarloFingerprint])

  useEffect(() => {
    let cancelled = false
    loadStoredProject(projectBackend)
      .then((loaded) => {
        if (cancelled) return
        persistenceBlockedRef.current = !loaded.writable
        applyProject(loaded.project)
        setProjectNotice(loaded.warning)
        setProjectError(loaded.writable ? null : loaded.warning)
        setHydrated(true)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        persistenceBlockedRef.current = true
        setProjectError(
          `Project storage could not be read and was left unchanged. ${error instanceof Error ? error.message : String(error)}`,
        )
        setHydrated(true)
      })
    return () => {
      cancelled = true
    }
  }, [applyProject, projectBackend])

  useEffect(() => {
    if (!hydrated) return
    const project = buildCurrentProject()
    const hasEvidence = projectHasEvidence(project)
    setUnpersistedEvidence(hasEvidence)
    if (persistenceBlockedRef.current) return
    const generation = ++saveGenerationRef.current
    saveQueue.enqueue(project)
      .then(() => {
        if (saveGenerationRef.current !== generation) return
        setUnpersistedEvidence(false)
        setProjectError((current) => current?.startsWith('Project save failed:') ? null : current)
      })
      .catch((error: unknown) => {
        if (saveGenerationRef.current !== generation) return
        setProjectError(`Project save failed: ${error instanceof Error ? error.message : String(error)}`)
      })
  }, [hydrated, buildCurrentProject, saveQueue])

  useEffect(() => {
    if (!unpersistedEvidence) return
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [unpersistedEvidence])

  const importProject = useCallback(async (file: File) => {
    try {
      const parsed = parseProjectJson(await file.text())
      if (!parsed.project) {
        setProjectError(`Project import rejected: ${parsed.errors.join(' ')}`)
        return
      }
      applyProject(parsed.project, true)
      setProjectError(null)
      setProjectNotice(`Imported ${file.name}. The complete project will be saved locally.`)
    } catch (error) {
      setProjectError(`Project import failed: ${error instanceof Error ? error.message : String(error)}`)
    }
  }, [applyProject])

  const exportProject = useCallback(() => {
    const project = buildCurrentProject()
    const date = project.savedAt.slice(0, 10)
    downloadText(
      `lenticular-display-project-${date}.json`,
      exportProjectJson(project),
      'application/json',
    )
    setProjectNotice('Exported the current inputs and completed evidence as project JSON.')
  }, [buildCurrentProject])

  if (!hydrated) {
    return (
      <div className="app-shell">
        <main id="main-content" className="main-content" aria-busy="true">
          <div className="notice info" role="status">Loading the saved project…</div>
        </main>
      </div>
    )
  }

  const selectedCandidateIsActive = Boolean(
    selectedCandidate &&
    candidateSimulationInputFingerprint(selectedCandidate, state) === simulationFingerprint,
  )
  const activeStatus = design.missing.length
    ? 'insufficient'
    : state.lens.mode === 'thin' && state.lens.thin.useDesignTarget
      ? 'design-target'
      : selectedCandidateIsActive && selectedCandidateEvaluation
        ? selectedCandidateEvaluation.status
        : 'insufficient'

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to work area</a>
      <header className="topbar">
        <div className="brand-block">
          <div className="brand-mark" aria-hidden="true"><Layers3 size={20} /></div>
          <div>
            <strong>Lenticular Display Lab</strong>
            <span>Horizontal optical workbench · v1</span>
          </div>
        </div>
        <div className="top-metrics" aria-label="Current setup summary">
          <Metric label="Source pitch q" value={formatNumber(design.source.sourcePitchMm, 5)} unit="mm" />
          <Metric label="Reduced gap d" value={formatNumber(design.gap.reducedGapMm, 5)} unit="mm" />
          <Metric label="Required pitch" value={formatNumber(design.requiredPitchMm, 5)} unit="mm" />
          <StatusBadge status={activeStatus} />
        </div>
      </header>

      <aside className="sidebar">
        <HorizontalScrollRegion
          className="workbench-nav-scroll"
          label="Workbench navigation"
          activeSelector='[aria-current="page"]'
        >
          <nav aria-label="Workbench areas">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  className={activeTab === tab.id ? 'active' : ''}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon size={17} aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </HorizontalScrollRegion>
        <ProjectControls
          disabled={!hydrated}
          onExport={exportProject}
          onImport={importProject}
        />
        <div className="sidebar-note">
          <Beaker size={16} aria-hidden="true" />
          <p>Deterministic client-side optics. No backend, account, telemetry, or external numerical service.</p>
        </div>
      </aside>

      <main id="main-content" className="main-content">
        {projectError ? <div className="notice danger" role="alert">{projectError}</div> : null}
        {projectNotice && projectNotice !== projectError ? (
          <div className="notice info" role="status">{projectNotice}</div>
        ) : null}
        {evidenceNotice ? (
          <div className="notice success" role="status" aria-live="polite">{evidenceNotice}</div>
        ) : null}
        {activeTab !== 'model' ? (
          <QualificationRail
            inputs={qualificationInputs}
            activeTab={activeTab}
            onNavigate={setActiveTab}
          />
        ) : null}
        {activeTab === 'setup' ? (
          <SetupTab
            state={state}
            setState={setState}
            onClearExample={() => replaceWorkingState(emptyState())}
            onLoadExample={() => replaceWorkingState(structuredClone(defaultState))}
          />
        ) : null}
        {activeTab === 'required' ? <RequiredLensTab state={state} /> : null}
        {activeTab === 'candidates' ? (
          <CandidatesTab
            state={state}
            setState={setState}
            selectedId={selectedCandidateId}
            setSelectedId={setSelectedCandidateId}
            onGoToRay={(candidateId) => {
              loadedCandidateIdRef.current = candidateId
              simulationSubjectRef.current = null
              toleranceSubjectRef.current = null
              setEvidenceNotice(null)
              setActiveTab('simulation')
            }}
          />
        ) : null}
        {activeTab === 'simulation' ? (
          <RaySimulationTab
            state={state}
            setState={setState}
            result={worker.simulationResult}
            stale={simulationStale}
            runState={worker.runState}
            onRun={() => {
              toleranceSubjectRef.current = null
              const candidate = state.candidates.find(
                (current) => current.id === loadedCandidateIdRef.current,
              )
              simulationSubjectRef.current =
                candidate && candidateSimulationInputFingerprint(candidate, state) === simulationFingerprint
                  ? { kind: 'simulation', candidateId: candidate.id, inputFingerprint: simulationFingerprint }
                  : null
              setEvidenceNotice(null)
              worker.startSimulation(state, state.lens.mode, simulationFingerprint)
            }}
            onCancel={() => {
              simulationSubjectRef.current = null
              worker.cancel()
            }}
          />
        ) : null}
        {activeTab === 'tolerances' ? (
          <TolerancesTab
            state={state}
            setState={setState}
            result={worker.monteCarloResult}
            stale={monteCarloStale}
            runState={worker.runState}
            onRun={() => {
              simulationSubjectRef.current = null
              const candidate = state.candidates.find(
                (current) => current.id === loadedCandidateIdRef.current,
              )
              toleranceSubjectRef.current =
                candidate && candidateToleranceInputFingerprint(candidate, state) === toleranceFingerprint
                  ? { kind: 'tolerance', candidateId: candidate.id, inputFingerprint: toleranceFingerprint }
                  : null
              setEvidenceNotice(null)
              worker.startMonteCarlo(state, toleranceFingerprint)
            }}
            onCancel={() => {
              toleranceSubjectRef.current = null
              worker.cancel()
            }}
          />
        ) : null}
        {activeTab === 'calibration' ? (
          <CalibrationTab
            state={state}
            setState={setState}
            dataset={calibrationDataset}
            setDataset={setCalibrationDataset}
            fit={calibrationFit}
            fitStale={calibrationFitStale}
            onFit={(fit) => {
              setCalibrationFit(fit)
              setCalibrationFitFingerprint(calibrationFingerprint)
            }}
            simulationResult={worker.simulationResult}
            simulationStale={simulationStale}
          />
        ) : null}
        {activeTab === 'model' ? <ModelTab /> : null}
      </main>
    </div>
  )
}

export default App
